<template>
<div>
  <form @submit.prevent="infoSave" v-if="step == STEPS.INFO" class="card--transparent user-onboarding">
    <h1>Welcome</h1>
    <div class="card--container">
      <h3 class="text-align: center">Start by creating your account</h3>
      <h-input name="first_name" required label="First Name" v-model="user.first_name" />
      <h-input name="last_name" required label="Last Name" v-model="user.last_name" />
      <h-input name="email" required label="Email" v-model="user.email" />
      <h-input name="password" required type="password" label="Password" v-model="user.password" />
      <div class="error-msg" v-if="user.password && user.confirm_password && (user.confirm_password !== user.password)">
        Confirmation password does not match
      </div>
      <h-input name="confirm_password" required type="password" label="Confirm Password" v-model="user.confirm_password" />
      <div >
        By creating an account you agree to our
        <a href="/terms-of-service" target="_blank">Terms of Service</a>.
      </div>
    </div>
    <button 
      type="submit" 
      class="submit"
      :disabled="!user.password || user.password !== user.confirm_password">Next</button>
  </form>


  <form @submit.prevent="companySave" v-if="step == STEPS.COMPANY" class="card--transparent user-onboarding">
    <h1>
      <h-icon v-if="!loggedIn" class="clickable" @click="step = STEPS.INFO">arrow_back</h-icon>
      Welcome
    </h1>
    <div class="card--container">
      <h3 class="text-align: center">
        We built Habit to be customizable to your brand. So let's set you up with your own subdomain, and brand the web app with your logo and colors.
      </h3>
      <div class="error-msg" v-if="subDomainError">
        {{subDomainError}}
      </div>
      <h-input id="sub_domain" @keypress="check" label="Your Subdomain" v-model="company.sub_domain" />
      <div class="text-info">
        Your full path: <b>https://{{company.sub_domain || "app" }}.habittechnologies.com</b>
      </div>
      <h-file :base64="true" @upload="upload => { company.logo = upload; updateCompany() }" label="Logo" />
      <div class="input">
        <label>Logo Size</label>
        <v-slider v-model="company.logo_height" @change="updateCompany"></v-slider>
      </div>
      <div class="input">
        <label>Navigation Bar Color</label>
        <swatches :value="company.header_color" @input="c => {company.header_color = c.hex; updateCompany()}" class="full-width small-top" />
      </div>

      <div class="error-msg" v-if="error">
        We could not process your request.
        <span v-if="errorType == 'stripe'">
          Error: {{error.message}} 
        </span>
        <span v-else-if="errorType == 'user'">
          Error: {{ error }}
        </span>
        <span v-else>
          There was a server error. Our staff has been notified and we should have this fixed this soon. 
        </span>
      </div>

    </div>
    <button v-if="loading" :disabled="loading" type="submit" class="submit">Loading</button>
    <button v-else type="submit" class="submit">Submit</button>
  </form>

  <div v-if="step == STEPS.FIN" >
    <div class="card--transparent user-onboarding">
      <h1>
        Success!
      </h1>
      <div class="card--container">
        <h2 style="text-align: center">
          Well Done!
        </h2>
        <h3 class="small-top" style="text-align: center">
          We're now going to take you to your custom subdomain.
          Once there, be sure to book the site; it's where you and 
          your clients will be logging into.
        </h3>
      </div>
      <button class="submit" @click="finish">Finish</button>
    </div>
  </div>
</div>
</template>

<script>
import AuthUser from "@/services/api/authUser";
import axios from "axios";
import { Swatches } from "vue-color";
import chooseHabitProgram from "@/pages/account/components/chooseHabitProgram";
import weekBtns from "@/components/weekBtns";
import timepicker from "@/components/timepicker";
import moment from "moment-timezone";
import subDomainHub from "@/services/hubs/subDomain"

const STEPS = {
  INFO: "info",
  COMPANY: "company",
  FIN: "finish"
};

export default {
  components: {
    Swatches,
    chooseHabitProgram,
    weekBtns,
    timepicker
  },
  data() {
    return {
      STEPS,
      step: STEPS.INFO,
      error: "",
      errorType: "",
      logggedIn: false,
      user: {
        has_coach_license: true,
        work_week: [0, 1, 2, 3, 4],
        generate_pulse_on: [0, 1, 2, 3, 4],
        timezone: moment.tz.guess(),
        generate_exercises_at: "10:00",
        generate_pulse_at: "18:00"
      },
      clientProfile: {
        areas_of_focus: []
      },
      company: {
        header_color: "#fffff",
        logo_height: 50,
        logo: '',
      },
      newUser: {},
      saving: false,
      loading: false,
      subDomainError: ""
    };
  },
  created() {
    this.loggedIn = AuthUser.isLoggedIn();
    if (this.loggedIn) {
      this.step = STEPS.COMPANY;
    }
    axios.get(`owners/offerings/${this.$route.params.offeringPromo}/`)
      .then(({data}) => {
        this.offering = data;
      });

    this.handler = StripeCheckout.configure({
      key: `${process.env.VUE_APP_STRIPE_KEY}`,
      image: "https://stripe.com/img/documentation/checkout/marketplace.png",
      locale: "auto",
      panelLabel: "Add Payment Method",
      email: this.email,
      token: token => {
        this.createAccount(token);
      }
    });
  },
  methods: {
    updateCompany() {
      subDomainHub.$emit("update", this.company);
    },
    infoSave() {
      this.step = this.STEPS.COMPANY;
    },
    check(event) {
      if (!event.key) {
        return;
      }
      const IGNORE_KEY = [ "enter", "tab", "delete", "escape", "left", "right", "up", "down", "backspace", "arrowleft", "arrowright", "arrowup", "arrowdown" ];
      if (IGNORE_KEY.includes(event.key)) {
        return;
      }
      if (!event.key.match(/[a-zA-Z1-9-]/)) {
        event.preventDefault();
      }
    },
    companySave() {
      this.loading = true;
      this.subDomainError = "";
      axios
        .get(`${process.env.VUE_APP_API_URL}sub-domains/${this.company.sub_domain}/`)
        .then(() => {
          this.subDomainError = "This sub domain is already in use";
        })
        .catch(() => {
          this.open();
        })
        .finally(() => {
          this.loading = false;
        });
    },
    open() {
      if (this.offering.amount) {
        this.handler.open({
          name: "HabitTechnologies.com"
        });
      } else {
        console.log('im getting here')
        this.createAccount({});
      }
    },
    createAccount(token) {
      let token_id = token.id;
      let card = token.card;
      this.saving = true;
      let data = {
        promo_id: this.$route.params.offeringPromo,
        user: this.user,
        client_profile: this.clientProfile,
        stripe_token: token_id,
        card: card,
        tos: true,
        company: this.company,
      }
      this.saving = true;
      let url = AuthUser.isLoggedIn() ? `${process.env.VUE_APP_API_URL}owners/authed/` : `${process.env.VUE_APP_API_URL}owners/new/`
      axios
        .post(url, data)
        .catch(({ response }) => {
          if (response.status === 402) {
            this.error = response.data;
            this.errorType = "stripe";
          } else if (response.status === 400) {
            this.error = response.data;
            this.errorType = "user";
          } else {
            this.errorType = "server";
          }
          throw response.data;
        })
        .then(() => {
          if (!AuthUser.isLoggedIn()) {
            return AuthUser.login(this.user);
          }
        }).then(() => {
          this.step = STEPS.FIN
        }).finally(() => {
          this.saving = false;
        })
    },
    finish() {
      window.location = `https://${this.company.sub_domain}.habittechnologies.com?next=getting-started?weclome=true`;
    },
  }
};
</script>

<style lang="less" scoped>
@import "../../../styles/colors";
.user-onboarding {
  position: relative;
  max-width: 600px;
  margin: 0px auto;
  .submit {
    background-color: @primaryColor;
    border-bottom-left-radius: 20px;
    border-bottom-right-radius: 20px;
    color: #FFFFFF;
    font-family: 'Quicksand', sans-serif;
    font-size: 20px;
    font-weight: 500;
    letter-spacing: 0;
    text-align: center;
    text-transform: uppercase;
    width: 100%;
  }
  
  .big-top {
    margin-top: 25px;
  }
  h1 {
    width: 100%;
    background-color: @primaryColor;
    border-top-left-radius: 20px;
    border-top-right-radius: 20px;
    text-align: center;
    i {
      position: absolute;
      top: 5px;
      left: 10px;
    }
  }

  h3, h4 {
    font-family: sans-serif;
    font-weight: 100;
  }
}
</style>
